/**
 * Frontend Logger Service
 * Captures and stores frontend logs to database for monitoring
 */

import { urlConfig } from '../config/urlConfig.js';

class FrontendLogger {
  constructor() {
    this.baseURL = urlConfig.api.baseURL + '/api';
    this.sessionId = this.generateSessionId();
    this.logQueue = [];
    this.isOnline = navigator.onLine;
    this.batchSize = 10;
    this.flushInterval = 300000; // 5 minutes - PERFORMANCE FIX: reduced from 60s
    this.maxQueueSize = 100;
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
    this.requestTimeout = 10000; // 10 seconds timeout
    
    // Circuit breaker pattern to prevent overloading failing server
    this.failureCount = 0;
    this.maxFailures = 5;
    this.circuitBreakerTimeout = 30000; // 30 seconds
    this.isCircuitOpen = false;
    this.lastFailureTime = null;
    
    this.setupEventListeners();
    this.startBatchFlush();
  }

  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get stored user data (from BaseService)
  getUser() {
    const userStr = localStorage.getItem('sanctuary_user');
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  // Get auth token as BaseService does
  getAuthToken() {
    return localStorage.getItem('sanctuary_token') || localStorage.getItem('authToken') || localStorage.getItem('token');
  }

  // Simple HTTP request method (minimal version of BaseService.request)
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    };

    // Add body if provided
    if (options.body) {
      config.body = options.body;
    }

    // Add signal if provided (for timeout)
    if (options.signal) {
      config.signal = options.signal;
    }

    const token = this.getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, config);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { success: true };
    }

    // Try to parse JSON, fallback to text if fails
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    return { success: true, message: await response.text() };
  }

  // Browser info detection (from BaseService)
  getBrowserInfo() {
    const ua = navigator.userAgent;
    let browserName = 'Unknown';
    let browserVersion = 'Unknown';

    if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) {
      browserName = 'Chrome';
      const match = ua.match(/Chrome\/([0-9.]+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (ua.indexOf('Firefox') > -1) {
      browserName = 'Firefox';
      const match = ua.match(/Firefox\/([0-9.]+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
      browserName = 'Safari';
      const match = ua.match(/Safari\/([0-9.]+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (ua.indexOf('Edg') > -1) {
      browserName = 'Edge';
      const match = ua.match(/Edg\/([0-9.]+)/);
      browserVersion = match ? match[1] : 'Unknown';
    }

    return {
      name: browserName,
      version: browserVersion,
      userAgent: ua,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      url: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString()
    };
  }

  setupEventListeners() {
    // Monitor network status
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushLogs();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Capture unhandled errors
    window.addEventListener('error', (event) => {
      const filename = event.filename || '';

      // Ignore browser extension errors
      if (
        filename.includes('chrome-extension://') ||
        filename.includes('moz-extension://') ||
        filename.includes('safari-extension://') ||
        filename.includes('ms-browser-extension://')
      ) {
        return;
      }

      // Ignore errors from third-party scripts (Stripe, analytics, CDNs, etc.)
      // Only log errors from our own origin or inline scripts (no filename)
      if (filename && !filename.startsWith(window.location.origin)) {
        return;
      }

      this.error('Unhandled JavaScript Error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        source: 'window.error'
      });
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      const reasonStr = reason?.toString() || '';

      // Filter out browser extension promise rejections
      if (reasonStr.includes('chrome-extension://') ||
          reasonStr.includes('moz-extension://') ||
          reasonStr.includes('safari-extension://') ||
          reasonStr.includes('ms-browser-extension://')) {
        return;
      }

      // Supabase auth-js uses the browser Navigator Lock API to serialize token
      // refresh across tabs. When it times out acquiring the lock it throws
      // { isAcquireTimeout: true } as an unhandled rejection. This is a known,
      // benign behaviour — there is nothing actionable about it.
      if (reason?.isAcquireTimeout === true) {
        event.preventDefault(); // also suppress the browser console warning
        return;
      }

      // Error objects are non-enumerable — JSON.stringify turns them into {}.
      // Serialize manually so the log actually contains useful information.
      let serializedReason;
      if (reason instanceof Error) {
        serializedReason = {
          name: reason.name,
          message: reason.message,
          stack: reason.stack
        };
      } else {
        serializedReason = reason;
      }

      this.error('Unhandled Promise Rejection', {
        reason: serializedReason,
        promise: event.promise?.toString(),
        source: 'unhandledrejection'
      });
    });

    // Flush logs before page unload
    window.addEventListener('beforeunload', () => {
      this.flushLogs(true);
    });
  }

  startBatchFlush() {
    // PERFORMANCE FIX: Check if circuit breaker is open before starting interval
    if (this.isCircuitOpen) {
      console.warn('Frontend logger circuit breaker is open, delaying batch flush');
      return;
    }
    
    this.flushTimer = setInterval(() => {
      // PERFORMANCE FIX: Skip flush if circuit breaker is open
      if (this.isCircuitOpen || this.logQueue.length === 0) {
        return;
      }
      this.flushLogs();
    }, this.flushInterval);
  }

  createLogEntry(level, message, context = {}, source = null) {
    const user = this.getUser();
    
    // Enhanced source detection from call stack
    if (!source) {
      const stack = new Error().stack;
      const lines = stack?.split('\n') || [];
      
      // Try to extract meaningful source from stack trace
      for (let i = 2; i < lines.length; i++) {
        const line = lines[i];
        if (line.includes('/src/') && !line.includes('logger') && !line.includes('Logger')) {
          // Extract file path and line number
          const match = line.match(/\/src\/(.+?):(\d+):(\d+)/);
          if (match) {
            const [, filePath, lineNumber] = match;
            // Clean up the path to get just the meaningful part
            source = filePath.replace(/^(sanctuary\/)?/, '') + `:${lineNumber}`;
            break;
          }
        }
      }
      
      // Fallback: try to extract just the file name
      if (!source) {
        for (let i = 2; i < lines.length; i++) {
          const line = lines[i];
          const fileMatch = line.match(/([^/\\]+\.(js|jsx|ts|tsx)):/);
          if (fileMatch) {
            source = fileMatch[1];
            break;
          }
        }
      }
      
      source = source || 'unknown';
    }

    return {
      user_id: user?.id || null,
      session_id: this.sessionId,
      log_level: level,
      message: typeof message === 'string' ? message : JSON.stringify(message),
      source,
      context: {
        ...context,
        browser_info: this.getBrowserInfo()
      },
      stack_trace: level === 'error' ? new Error().stack : null,
      url: window.location.href,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };
  }

  addToQueue(logEntry) {
    this.logQueue.push(logEntry);
    
    // Prevent memory leaks by limiting queue size
    if (this.logQueue.length > this.maxQueueSize) {
      this.logQueue = this.logQueue.slice(-this.maxQueueSize);
    }

    // Only immediate flush for critical errors, not all errors
    if (logEntry.log_level === 'error' && this.logQueue.length >= this.batchSize) {
      this.flushLogs();
    }
  }

  async flushLogs(synchronous = false) {
    if (!this.isOnline || this.logQueue.length === 0) {
      return;
    }

    // Check circuit breaker
    if (this.isCircuitOpen) {
      const now = Date.now();
      if (now - this.lastFailureTime < this.circuitBreakerTimeout) {
        return; // Circuit is open, don't attempt to send
      } else {
        // Try to close circuit after timeout
        this.isCircuitOpen = false;
        this.failureCount = 0;
      }
    }

    const logsToSend = this.logQueue.splice(0, this.batchSize);
    
    try {
      if (synchronous) {
        // Use sendBeacon for synchronous sending during page unload
        const blob = new Blob([JSON.stringify({ logs: logsToSend })], {
          type: 'application/json'
        });
        navigator.sendBeacon(`${this.baseURL}/frontend-logs/batch`, blob);
      } else {
        // Use timeout and retry logic for async requests
        await this.sendLogsWithRetry(logsToSend);
      }
      
      // Reset failure count on success
      this.failureCount = 0;
      
    } catch (error) {
      // Increment failure count and open circuit if needed
      this.failureCount++;
      this.lastFailureTime = Date.now();
      
      if (this.failureCount >= this.maxFailures) {
        this.isCircuitOpen = true;
      }
      
      // If sending fails, put logs back in queue (at the beginning)
      this.logQueue.unshift(...logsToSend);
      
      // Only log to console in development to avoid recursion
      if (import.meta.env.DEV) {
        console.warn('Failed to send logs to server:', error.message);
      }
    }
  }

  async sendLogsWithRetry(logs, retryCount = 0) {
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);

      await this.request('/frontend-logs/batch', {
        method: 'POST',
        body: JSON.stringify({ logs }),
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);
    } catch (error) {
      if (retryCount < this.maxRetries && !error.name === 'AbortError') {
        // Wait before retry with exponential backoff
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * Math.pow(2, retryCount)));
        return this.sendLogsWithRetry(logs, retryCount + 1);
      }
      throw error;
    }
  }

  // Public logging methods
  error(message, context = {}, source = null) {
    const logEntry = this.createLogEntry('error', message, context, source);
    this.addToQueue(logEntry);
    
    // Still show in console for development
    if (import.meta.env.DEV) {
      console.error(`[${source || 'Frontend'}] ${message}`, context);
    }
  }

  warn(message, context = {}, source = null) {
    const logEntry = this.createLogEntry('warn', message, context, source);
    this.addToQueue(logEntry);
    
    if (import.meta.env.DEV) {
      console.warn(`[${source || 'Frontend'}] ${message}`, context);
    }
  }

  info(message, context = {}, source = null) {
    const logEntry = this.createLogEntry('info', message, context, source);
    this.addToQueue(logEntry);
    
    if (import.meta.env.DEV) {
      console.info(`[${source || 'Frontend'}] ${message}`, context);
    }
  }

  debug(message, context = {}, source = null) {
    // Only log debug in development
    if (import.meta.env.DEV) {
      const logEntry = this.createLogEntry('debug', message, context, source);
      this.addToQueue(logEntry);
      console.debug(`[${source || 'Frontend'}] ${message}`, context);
    }
  }

  // Specialized logging methods
  apiError(endpoint, error, requestData = {}) {
    this.error(`API Request Failed: ${endpoint}`, {
      endpoint,
      error_message: error.message,
      error_stack: error.stack,
      request_data: requestData,
      response_status: error.status || 'unknown'
    }, 'api');
  }

  userAction(action, details = {}) {
    this.info(`User Action: ${action}`, {
      action,
      ...details,
      user_id: this.getUser()?.id
    }, 'user-action');
  }

  performance(metric, value, details = {}) {
    this.info(`Performance: ${metric}`, {
      metric,
      value,
      ...details
    }, 'performance');
  }

  // Method to manually flush all queued logs
  async flush() {
    while (this.logQueue.length > 0) {
      await this.flushLogs();
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  // PERFORMANCE FIX: Stop logging and cleanup intervals
  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    // Clear remaining logs
    this.logQueue = [];
  }
}

// Create singleton instance
export const frontendLogger = new FrontendLogger();